/**
 * Apps and dashboards people built: what exists, how it is shared and how
 * much it is used.
 *
 * The nightly record comes in two shapes. The older one holds counts of what
 * exists and nothing else: the tab then shows those counts and says that run
 * history has not started. The newer one adds a daily history, a row per app
 * and a list of the sources each stack has.
 *
 * A number whose source a stack does not have is not available, and reads
 * "n/a", never 0. One exception: on an app's own row a missing title, publish
 * time or run time means there is none.
 */
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Badge, Table } from 'react-bootstrap';
import { Bar as BarChart } from 'react-chartjs-2';
import '../chartSetup';
import { ND_COLORS } from '@/styles/theme';
import type { ClientSnapshot } from '@/types/fleetAnalytics';
import { formatNzDate, formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import { useCurrency } from '../currencyContext';
import {
  isReady,
  needsOf,
  selectActiveUsers,
  selectAppRows,
  selectApps,
  selectAppsByStack,
  selectSeries,
  selectSwitches,
} from '../selectors';
import type { AppRow, AppsFigures, AppsStackRow, AppsWindowKey, Figure, Tiered } from '../selectors';
import { dayWords, fmtN, fmtUSD, isInfraAccount } from '../shared';
import type { WindowState } from '../shared';
import { Empty, Kpi, KpiGrid, NeedsTier, Panel, Section } from '../ui/kit';
import {
  dayLabel,
  fmtCredits,
  gridCols,
  LinkButton,
  nextSort,
  plural,
  Quiet,
  SearchBox,
  sortRows,
  SortTh,
  tile,
  useSort,
} from './common';
import type { Sort, SortValue } from './common';
import type { DashboardTabProps, Selection } from './registry';

// ─── words and formats ───────────────────────────────────────────────────

/** A count of what exists. A count the record does not carry is not available. */
const count = (n: number | undefined) => (n === undefined ? 'n/a' : fmtN(n));

/**
 * Where the run history starts and ends, in a sentence. Stacks start on
 * different days, so a view has an earliest start and a day from which every
 * stack is complete. The start can move forward after the nightly update
 * misses a week.
 */
function historyWords(apps: AppsFigures): string {
  const from = apps.historyFrom;
  const fromEvery = apps.historyCompleteFrom ?? from;
  if (!from || !fromEvery) return 'No run history is on record.';
  let upTo = '';
  if (apps.asOf) {
    upTo =
      apps.asOfBehind && apps.asOfBehind !== apps.asOf
        ? ` Counted up to ${dayWords(apps.asOf)}, or ${dayWords(apps.asOfBehind)} for the stack furthest behind.`
        : ` Counted up to ${dayWords(apps.asOf)}.`;
  }
  if (from === fromEvery) return `Run counts are complete from ${dayWords(from)}.${upTo}`;
  return `Run counts are complete for every stack from ${dayWords(fromEvery)}. The earliest stack starts on ${dayWords(from)}.${upTo}`;
}

// ─── the tab ─────────────────────────────────────────────────────────────

export function Apps({ data, window, fleet, selection, onSelectClient }: DashboardTabProps) {
  useCurrency();
  const stacks = selection.stacks;
  const apps = useMemo(() => selectApps(data, window), [data, window]);
  const activePeople = useMemo(() => selectActiveUsers(data, window), [data, window]);
  const isView = selection.kind === 'view';
  // False on the older record, and on a stack that keeps no record of app runs.
  const hasRuns = Boolean(apps?.window && apps.window.runs.value !== null);

  // "Apps used" and "People who used an app" count each app and each person once, which needs the per-app record.
  const { ensureTier } = fleet;
  useEffect(() => {
    if (hasRuns) needsOf(apps?.appsRun, apps?.people, activePeople).forEach(ensureTier);
  }, [hasRuns, apps, activePeople, ensureTier]);

  if (!apps || apps.inventory.total === 0) return <NoApps selection={selection} />;

  const inv = apps.inventory;
  const behind = apps.stacksWithoutHistory;
  const behindNote = behind.length
    ? `${plural(behind.length, 'stack keeps', 'stacks keep')} no run history yet and ${behind.length === 1 ? 'is' : 'are'} left out.`
    : '';

  // The newer record splits the published ones. The older one splits every record, drafts included.
  const splitsPublished = !behind.length && inv.published_apps !== undefined && inv.published_dashboards !== undefined;
  let publishedSub: string;
  if (inv.draft_only === 0) {
    publishedSub = `${plural(inv.apps, 'app')} and ${plural(inv.dashboards, 'dashboard')}. None is a draft.`;
  } else if (splitsPublished) {
    publishedSub = `${plural(inv.published_apps, 'app')} and ${plural(inv.published_dashboards, 'dashboard')}. ${plural(inv.draft_only, 'more is a draft', 'more are drafts')} not yet published.`;
  } else {
    publishedSub = `${plural(inv.apps, 'app')} and ${plural(inv.dashboards, 'dashboard')} in all, drafts included. ${plural(inv.draft_only, 'draft is', 'drafts are')} not yet published.`;
  }

  return (
    <>
      {/* What happened in the window, and its chart. Shown once the record keeps a run history. */}
      {apps.window && (
        <UseSection
          apps={apps}
          figures={apps.window}
          hasRuns={hasRuns}
          activePeople={activePeople}
          note={[
            behindNote,
            apps.historyCompleteFrom && window.startDate < apps.historyCompleteFrom
              ? `The window starts before the run history does, on ${dayWords(apps.historyCompleteFrom)}, so these figures read low.`
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <RunsChart data={data} window={window} apps={apps} hasRuns={hasRuns} />
        </UseSection>
      )}

      {/* What exists now, and the lists of it. */}
      <Section
        title="What exists"
        note={
          apps.window
            ? undefined
            : 'Run history starts with the next nightly update. These counts show what exists today.'
        }
      >
        <KpiGrid cols={!apps.hasHistory ? 3 : behind.length > 0 ? 5 : 4}>
          <Kpi
            label="Published"
            value={fmtN(inv.published)}
            scope="now"
            sub={publishedSub}
            hint="Apps and dashboards with a published version that people can open."
          />
          <Kpi
            label="People who built one"
            value={fmtN(inv.distinct_owners)}
            scope="now"
            sub={`${plural(inv.total, 'app or dashboard', 'apps and dashboards')} between them, drafts included`}
            hint="People who own an app or a dashboard, published or draft."
          />
          {apps.hasHistory && <SharedTile apps={apps} behindNote={behindNote} />}
          {/* Every record counts public links. The sharing count above comes from the newer record only. */}
          {(!apps.hasHistory || behind.length > 0) && (
            <Kpi
              label="With a public link"
              value={fmtN(inv.with_public_link)}
              scope="now"
              sub="Anybody with the link can open these"
            />
          )}
          {apps.window && <AutomationTile apps={apps} window={apps.window} behindNote={behindNote} />}
        </KpiGrid>

        {isView && <ByStack fleet={fleet} stacks={stacks} window={window} onSelectClient={onSelectClient} />}

        {apps.hasHistory && (
          <AppsPanel fleet={fleet} stacks={stacks} window={window} isView={isView} onSelectClient={onSelectClient} />
        )}
      </Section>
    </>
  );
}

/** A stack, or a view, with no app and no dashboard. One calm line, not a grid of zeros. */
function NoApps({ selection }: { selection: Selection }) {
  const stack = selection.kind === 'stack' ? selection.stacks[0] : undefined;
  const builder = stack ? selectSwitches(stack).appBuilder : null;
  return (
    <Panel title="Apps and dashboards">
      <Empty>
        No apps or dashboards yet.
        {builder === false &&
          ' App Builder is off for this stack, so its people can publish dashboards and cannot build apps.'}
        {builder === true && ' App Builder is on for this stack.'}
      </Empty>
    </Panel>
  );
}

// ─── figures ─────────────────────────────────────────────────────────────

/** How many published apps somebody other than the owner can open, and by which route. */
function SharedTile({ apps, behindNote }: { apps: AppsFigures; behindNote: string }) {
  const inv = apps.inventory;
  // Two sharing levels open an app to the whole company: to view, and to edit.
  const levels = inv.by_visibility;
  const parts: string[] = [];
  if (levels) parts.push(`${fmtN((levels.company || 0) + (levels.company_editor || 0))} with the whole company`);
  if (inv.named_shares !== undefined) parts.push(`${fmtN(inv.named_shares)} with named people`);
  // In a view with stacks left out, public links have a figure of their own that counts every stack.
  if (!behindNote) parts.push(`${fmtN(inv.with_public_link)} by public link`);
  // Where some stacks of the view do not report sharing, the true count is this or more: the figure is marked.
  return (
    <Kpi
      label="Shared beyond the owner"
      value={count(inv.shared)}
      scope="now"
      approx={Boolean(behindNote) && inv.shared !== undefined}
      note={behindNote || undefined}
      sub={parts.join(', ')}
      hint="Published apps and dashboards that somebody other than the owner can open inside the company."
    />
  );
}

interface AutomationTileProps {
  apps: AppsFigures;
  window: NonNullable<AppsFigures['window']>;
  behindNote: string;
}

/** Published apps a schedule or an event runs. The count is as of now; the runs under it are the window's. */
function AutomationTile({ apps, window, behindNote }: AutomationTileProps) {
  const runs = window.automationRuns.value;
  const failures = window.automationFailures.value;
  return (
    <Kpi
      label="With an automation"
      value={count(apps.inventory.with_automation)}
      scope="now"
      approx={Boolean(behindNote) && apps.inventory.with_automation !== undefined}
      note={behindNote || undefined}
      sub={
        runs === null
          ? 'Automation runs are not on record'
          : `${plural(runs, 'automation run')} in this window${failures === null ? '' : `, ${fmtN(failures)} failed`}`
      }
      hint="Published apps that a schedule or an event runs."
    />
  );
}

interface UseSectionProps {
  apps: AppsFigures;
  figures: NonNullable<AppsFigures['window']>;
  hasRuns: boolean;
  activePeople: Tiered<Figure>;
  note: string;
  /** The chart under the strip. */
  children: ReactNode;
}

/** What happened in the window: runs, the people behind them, what was made, what it cost. */
function UseSection({ apps, figures, hasRuns, activePeople, note, children }: UseSectionProps) {
  const inv = apps.inventory;
  const value = (key: AppsWindowKey) => figures[key].value;
  const buildRuns = value('buildRuns');
  const publishes = value('publishes');
  const deleted = value('deletedApps');
  const cost = value('costUsd');
  const buildCost = value('buildCostUsd');
  const views = value('publicViews');
  const submissions = value('submissions');
  // Left out when no app has a public link and nobody has opened or sent anything.
  const hasPublicPages =
    inv.with_public_link > 0 || (views !== null && views > 0) || (submissions !== null && submissions > 0);

  return (
    <Section title="Use in this window" note={note || undefined}>
      {/* Three figures always, two more with a run record, one more with public pages. */}
      <KpiGrid cols={gridCols(3 + (hasRuns ? 2 : 0) + (hasPublicPages ? 1 : 0))}>
        {hasRuns && <Kpi label="Apps used" {...tile(apps.appsRun, fmtN)} sub={`of ${fmtN(inv.published)} published`} />}
        <Kpi
          lead
          label="Runs"
          {...tile(
            figures.runs,
            fmtN,
            'A run is a published app or dashboard run by a person or by an automation. Draft and test runs are counted apart.'
          )}
          sub={
            !hasRuns
              ? 'No record of app runs is kept here'
              : buildRuns === null
                ? undefined
                : `${plural(buildRuns, 'more run', 'more runs')} while building`
          }
        />
        {hasRuns && (
          <Kpi
            label="People who used an app"
            {...tile(
              apps.people,
              fmtN,
              'People who started a run themselves. A run an automation started counts for nobody.'
            )}
            sub={
              isReady(activePeople)
                ? `${plural(activePeople.value, 'person was', 'people were')} active in chat`
                : undefined
            }
          />
        )}
        <Kpi
          label="New apps"
          {...tile(figures.newApps, fmtN, 'Apps and dashboards made in the window, drafts included.')}
          sub={
            publishes === null
              ? undefined
              : `${plural(publishes, 'publish', 'publishes')}${deleted !== null && deleted > 0 ? `, ${fmtN(deleted)} deleted` : ''}`
          }
        />
        <Kpi
          label="Credits from apps"
          {...tile(
            figures.credits,
            fmtCredits,
            'Credits charged for the AI steps of app runs. Building an app is chat, and its cost is shown apart.'
          )}
          sub={
            cost === null
              ? 'No credit record is kept here'
              : `${fmtUSD(cost)} of model use${buildCost === null ? '' : `. ${fmtUSD(buildCost)} spent building.`}`
          }
        />
        {hasPublicPages && (
          <Kpi
            label="Public page views"
            {...tile(figures.publicViews, fmtN, 'Times somebody opened an app or a dashboard by its public link.')}
            sub={submissions === null ? undefined : plural(submissions, 'form submission')}
          />
        )}
      </KpiGrid>
      {children}
    </Section>
  );
}

// ─── runs per day ────────────────────────────────────────────────────────

interface RunsChartProps {
  data: DashboardTabProps['data'];
  window: WindowState;
  apps: AppsFigures;
  hasRuns: boolean;
}

function RunsChart({ data, window, apps, hasRuns }: RunsChartProps) {
  const series = useMemo(() => selectSeries(data, window, 'appRuns'), [data, window]);
  const from = apps.historyFrom;
  const upTo = apps.asOf;

  if (!hasRuns || !isReady(series) || !series.available || !from) {
    return (
      <Panel title="Runs per day">
        <Empty>No record of app runs is kept here.</Empty>
      </Panel>
    );
  }
  // Only the days the record covers are drawn. A day outside it is unknown, not zero.
  const days: string[] = [];
  const values: number[] = [];
  series.days.forEach((day, i) => {
    if (day < from || (upTo && day > upTo)) return;
    days.push(day);
    values.push(series.values[i]);
  });

  return (
    <Panel title="Runs per day" subtitle={historyWords(apps)}>
      {days.length === 0 ? (
        <Empty>This window has no day inside the run history.</Empty>
      ) : (
        <div
          className="dash-chart"
          role="img"
          aria-label="Bar chart of app runs for each day of the run history in the window"
        >
          <BarChart
            data={{
              labels: days.map(dayLabel),
              datasets: [
                {
                  label: 'Runs',
                  data: values,
                  backgroundColor: ND_COLORS.accent,
                  borderRadius: 4,
                  maxBarThickness: 24,
                },
              ],
            }}
            options={{
              maintainAspectRatio: false,
              interaction: { mode: 'index', intersect: false },
              plugins: { legend: { display: false } },
              scales: {
                x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 14 } },
                y: { beginAtZero: true, ticks: { precision: 0 } },
              },
            }}
          />
        </div>
      )}
    </Panel>
  );
}

// ─── which stacks use apps (a view only) ─────────────────────────────────

type StackColumn = 'label' | 'apps' | 'dashboards' | 'published' | 'runs' | 'people' | 'credits';
const STACK_WORD_COLUMNS: ReadonlySet<StackColumn> = new Set<StackColumn>(['label']);

const STACK_VALUE: Record<StackColumn, (r: AppsStackRow) => SortValue> = {
  label: (r) => r.label,
  apps: (r) => r.apps,
  dashboards: (r) => r.dashboards,
  published: (r) => r.published,
  runs: (r) => r.runs,
  people: (r) => r.people,
  credits: (r) => r.credits,
};

interface ByStackProps {
  fleet: DashboardTabProps['fleet'];
  stacks: ClientSnapshot[];
  window: WindowState;
  onSelectClient: DashboardTabProps['onSelectClient'];
}

function ByStack({ fleet, stacks, window, onSelectClient }: ByStackProps) {
  const answer = useMemo(() => selectAppsByStack(stacks, window), [stacks, window]);
  const rows = answer.rows;
  const anyHistory = rows.some((r) => r.hasHistory);
  // The people column needs each person's runs, which load on their own.
  const peopleLoading = answer.pending.length > 0;
  const { ensureTier } = fleet;
  useEffect(() => {
    needsOf(answer).forEach(ensureTier);
  }, [answer, ensureTier]);

  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Sort<StackColumn> | null>(null);
  // Until a person picks a column: the busiest stack first, or the one with the most published when nothing is counted yet.
  const sort: Sort<StackColumn> = picked ?? { key: anyHistory ? 'runs' : 'published', dir: 'desc' };
  const stackCount = stacks.filter((s) => !isInfraAccount(s)).length;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matching = q ? rows.filter((r) => r.label.toLowerCase().includes(q)) : rows;
    // Within one value of the sorted column, the stack with the most published comes first.
    return sortRows(sortRows(matching, STACK_VALUE.published, 'desc'), STACK_VALUE[sort.key], sort.dir);
  }, [rows, query, sort.key, sort.dir]);

  const onSort = (key: StackColumn) => setPicked(nextSort(sort, key, STACK_WORD_COLUMNS));
  const cell = (n: number | null, format: (n: number) => string = fmtN) => (n === null ? <Quiet /> : format(n));

  return (
    <Panel
      title="Stacks with apps"
      subtitle={`${fmtN(rows.length)} of ${plural(stackCount, 'stack has', 'stacks have')} an app or a dashboard. Select a row to open the stack.`}
      flush
      actions={
        rows.length > 30 ? <SearchBox value={query} onChange={setQuery} placeholder="Search stacks" /> : undefined
      }
    >
      <div className="dash-scroll">
        <Table className="dash-table">
          <thead>
            <tr>
              <SortTh column="label" sort={sort} onSort={onSort}>
                Stack
              </SortTh>
              <SortTh
                column="apps"
                hint="Every app on record, drafts included."
                numeric
                scope="now"
                sort={sort}
                onSort={onSort}
              >
                Apps
              </SortTh>
              <SortTh
                column="dashboards"
                hint="Every dashboard on record, drafts included."
                numeric
                scope="now"
                sort={sort}
                onSort={onSort}
              >
                Dashboards
              </SortTh>
              <SortTh column="published" numeric scope="now" sort={sort} onSort={onSort}>
                Published
              </SortTh>
              {anyHistory && (
                <>
                  <SortTh column="runs" numeric sort={sort} onSort={onSort}>
                    Runs
                  </SortTh>
                  <SortTh
                    column="people"
                    hint="People who started an app run in the window."
                    numeric
                    sort={sort}
                    onSort={onSort}
                  >
                    People
                  </SortTh>
                  <SortTh column="credits" numeric sort={sort} onSort={onSort}>
                    Credits
                  </SortTh>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr>
                <td colSpan={anyHistory ? 7 : 4}>
                  <Empty>{rows.length ? 'No stack matches.' : 'No apps or dashboards yet.'}</Empty>
                </td>
              </tr>
            )}
            {visible.map((r) => (
              <tr key={r.client} className="dash-row" onClick={() => onSelectClient(r.client)}>
                <td>
                  {/* The row is the click target. The button gives it to the keyboard. */}
                  <LinkButton name title={`Open ${r.label}`}>
                    {r.label}
                  </LinkButton>
                </td>
                <td className="text-end numeric">{cell(r.apps)}</td>
                <td className="text-end numeric">{cell(r.dashboards)}</td>
                <td className="text-end numeric">{cell(r.published)}</td>
                {anyHistory && (
                  <>
                    <td className="text-end numeric">{cell(r.runs)}</td>
                    <td className="text-end numeric">
                      {r.people === null && r.hasHistory && peopleLoading ? <Quiet>…</Quiet> : cell(r.people)}
                    </td>
                    <td className="text-end numeric">{cell(r.credits, fmtCredits)}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </Panel>
  );
}

// ─── every app and dashboard ─────────────────────────────────────────────

type AppColumn =
  | 'title'
  | 'client'
  | 'type'
  | 'owner'
  | 'versions'
  | 'published'
  | 'lastRun'
  | 'runs'
  | 'people'
  | 'credits';
const APP_WORD_COLUMNS: ReadonlySet<AppColumn> = new Set<AppColumn>(['title', 'client', 'type', 'owner']);

const typeWords = (r: AppRow) => (r.type === 'dashboard' ? 'Dashboard' : 'App');

/** Who can open the app, in a few words. A draft is open to nobody but its owner. */
function sharingWords(r: AppRow): string {
  if (!r.published) return 'Draft, not yet published';
  const parts: string[] = [];
  if (r.visibility === 'company' || r.visibility === 'company_editor') parts.push('Whole company');
  else if (r.visibility === 'shared' || (r.named_shares !== undefined && r.named_shares > 0))
    parts.push('Named people');
  else parts.push('Owner only');
  if (r.public_intake) parts.push('public form');
  else if (r.public_link) parts.push('public link');
  if (r.automations !== undefined && r.automations > 0) parts.push(plural(r.automations, 'automation'));
  return parts.join(', ');
}

const APP_VALUE: Record<AppColumn, (r: AppRow) => SortValue> = {
  title: (r) => r.title || r.id,
  client: (r) => r.client,
  type: (r) => typeWords(r),
  owner: (r) => r.ownerLabel,
  versions: (r) => r.versions,
  published: (r) => (r.published ? r.last_published_at : null),
  lastRun: (r) => r.last_run_at,
  runs: (r) => r.runsInWindow,
  people: (r) => r.peopleInWindow,
  credits: (r) => r.creditsInWindow,
};

/** A time as "3 d ago" with the New Zealand date under it, or a quiet word when there is none. */
function When({ at, none }: { at?: string; none: string }) {
  if (!at) return <Quiet>{none}</Quiet>;
  return (
    <div title={formatNzDateTime(at)}>
      {timeAgo(at)}
      <div className="dash-sub">{formatNzDate(at)}</div>
    </div>
  );
}

interface AppsPanelProps {
  fleet: DashboardTabProps['fleet'];
  stacks: ClientSnapshot[];
  window: WindowState;
  isView: boolean;
  onSelectClient: DashboardTabProps['onSelectClient'];
}

function AppsPanel({ fleet, stacks, window, isView, onSelectClient }: AppsPanelProps) {
  const answer = useMemo(() => selectAppRows(stacks, window), [stacks, window]);
  const [query, setQuery] = useState('');
  const { sort, onSort } = useSort<AppColumn>({ key: 'runs', dir: 'desc' }, APP_WORD_COLUMNS);

  // Owners read as an email once the list of people is loaded.
  const { ensureTier } = fleet;
  useEffect(() => {
    needsOf(answer).forEach(ensureTier);
  }, [answer, ensureTier]);

  const all = isReady(answer) ? answer.rows : null;
  // A column is drawn only when one row or more has a value for it.
  const showRuns = Boolean(all?.some((r) => r.runsInWindow !== null));
  const showLastRun = Boolean(all?.some((r) => r.last_run_at || r.runsInWindow !== null));
  const showPeople = Boolean(all?.some((r) => r.peopleInWindow !== null));
  const showCredits = Boolean(all?.some((r) => r.creditsInWindow !== null));

  const visible = useMemo(() => {
    if (!all) return [];
    const q = query.trim().toLowerCase();
    const rows = q
      ? all.filter((r) =>
          [r.title, r.ownerLabel, r.client, typeWords(r)].some((v) => (v || '').toLowerCase().includes(q))
        )
      : all;
    return sortRows(rows, APP_VALUE[sort.key], sort.dir);
  }, [all, query, sort]);

  const columns =
    5 + (isView ? 1 : 0) + (showLastRun ? 1 : 0) + (showRuns ? 1 : 0) + (showPeople ? 1 : 0) + (showCredits ? 1 : 0);

  return (
    <Panel
      title="Apps and dashboards"
      subtitle={
        all ? `${fmtN(visible.length)} of ${plural(all.length, 'app or dashboard', 'apps and dashboards')}.` : undefined
      }
      flush
      actions={
        <SearchBox value={query} onChange={setQuery} placeholder="Search apps" label="Search apps and dashboards" />
      }
    >
      <NeedsTier fleet={fleet} tiers={all ? ['apps'] : needsOf(answer)} what="The list of apps">
        <div className="dash-scroll">
          <Table className="dash-table">
            <thead>
              <tr>
                <SortTh column="title" sort={sort} onSort={onSort}>
                  Name
                </SortTh>
                {isView && (
                  <SortTh column="client" sort={sort} onSort={onSort}>
                    Client
                  </SortTh>
                )}
                <SortTh column="type" sort={sort} onSort={onSort}>
                  Type
                </SortTh>
                <SortTh column="owner" sort={sort} onSort={onSort}>
                  Owner
                </SortTh>
                <SortTh column="versions" numeric scope="now" sort={sort} onSort={onSort}>
                  Versions
                </SortTh>
                <SortTh column="published" sort={sort} onSort={onSort}>
                  Last published
                </SortTh>
                {showLastRun && (
                  <SortTh column="lastRun" sort={sort} onSort={onSort}>
                    Last run
                  </SortTh>
                )}
                {showRuns && (
                  <SortTh column="runs" numeric sort={sort} onSort={onSort}>
                    Runs
                  </SortTh>
                )}
                {showPeople && (
                  <SortTh
                    column="people"
                    hint="People who started a run of it in the window. For a window that ends before today this can read high."
                    numeric
                    sort={sort}
                    onSort={onSort}
                  >
                    People
                  </SortTh>
                )}
                {showCredits && (
                  <SortTh column="credits" numeric sort={sort} onSort={onSort}>
                    Credits
                  </SortTh>
                )}
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr>
                  <td colSpan={columns}>
                    <Empty>
                      {all && all.length > 0 ? 'No app matches.' : 'The list of apps is not on record yet.'}
                    </Empty>
                  </td>
                </tr>
              )}
              {visible.map((r) => (
                <tr key={`${r.client}::${r.id}`}>
                  <td className="dash-wrap">
                    <div className="dash-name">
                      {r.title || <span className="mono">{r.id}</span>}
                      {!r.published && (
                        <Badge bg="secondary" className="ms-2">
                          Draft
                        </Badge>
                      )}
                    </div>
                    <div className="dash-sub">{sharingWords(r)}</div>
                  </td>
                  {isView && (
                    <td>
                      <LinkButton onClick={() => onSelectClient(r.client)} title={`Open ${r.client}`}>
                        {r.client}
                      </LinkButton>
                    </td>
                  )}
                  <td>
                    {typeWords(r)}
                    {r.kind === 'static' && <div className="dash-sub">A fixed page</div>}
                    {r.kind === 'headless' && <div className="dash-sub">No page</div>}
                  </td>
                  <td className="dash-clip" title={r.ownerLabel}>
                    {r.ownerLabel || <Quiet />}
                  </td>
                  <td className="text-end numeric">{r.versions === undefined ? <Quiet /> : fmtN(r.versions)}</td>
                  <td>
                    <When at={r.published ? r.last_published_at : undefined} none="Never" />
                  </td>
                  {showLastRun && (
                    <td>
                      {/* No run time on a stack that keeps a run record means no run is held. With no run record it is unknown. */}
                      {r.last_run_at || r.runsInWindow !== null ? (
                        <When at={r.last_run_at} none="No run on record" />
                      ) : (
                        <Quiet />
                      )}
                    </td>
                  )}
                  {showRuns && (
                    <td className="text-end numeric">{r.runsInWindow === null ? <Quiet /> : fmtN(r.runsInWindow)}</td>
                  )}
                  {showPeople && (
                    <td className="text-end numeric">
                      {r.peopleInWindow === null ? <Quiet /> : fmtN(r.peopleInWindow)}
                    </td>
                  )}
                  {showCredits && (
                    <td className="text-end numeric">
                      {r.creditsInWindow === null ? <Quiet /> : fmtCredits(r.creditsInWindow)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </NeedsTier>
    </Panel>
  );
}
